import { motion } from 'framer-motion';
import type { PaymentRecord } from '@/hooks/useAccounting';
import { theme } from '@/lib/theme';

interface Props {
  payments: PaymentRecord[];
  loading: boolean;
}

function formatCOP(amount: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const date = d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
  return { date, time };
}

function TypeBadge({ type }: { type: string }) {
  const isAdvance = type === 'advance';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap"
      style={{
        backgroundColor: isAdvance ? 'rgba(217,217,217,0.08)' : 'rgba(52,211,153,0.10)',
        color: isAdvance ? theme.colors.primary : '#34d399',
        border: `1px solid ${isAdvance ? 'rgba(217,217,217,0.15)' : 'rgba(52,211,153,0.20)'}`,
      }}
    >
      <div className="w-3 h-3 flex items-center justify-center">
        <i className={isAdvance ? 'ri-arrow-down-circle-line text-xs' : 'ri-check-double-line text-xs'} />
      </div>
      {isAdvance ? 'Anticipo' : 'Restante'}
    </span>
  );
}

function AppointmentStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; icon: string; color: string; bg: string; border: string }> = {
    completed: {
      label: 'Completado',
      icon: 'ri-checkbox-circle-line',
      color: '#34d399',
      bg: 'rgba(52,211,153,0.10)',
      border: 'rgba(52,211,153,0.20)',
    },
    no_show: {
      label: 'No asistió',
      icon: 'ri-user-unfollow-line',
      color: '#f87171',
      bg: 'rgba(248,113,113,0.10)',
      border: 'rgba(248,113,113,0.20)',
    },
    confirmed: {
      label: 'Confirmada',
      icon: 'ri-calendar-check-line',
      color: '#fbbf24',
      bg: 'rgba(251,191,36,0.10)',
      border: 'rgba(251,191,36,0.20)',
    },
    pending: {
      label: 'Pendiente',
      icon: 'ri-time-line',
      color: 'rgba(255,255,255,0.45)',
      bg: 'rgba(255,255,255,0.05)',
      border: 'rgba(255,255,255,0.10)',
    },
    cancelled: {
      label: 'Cancelada',
      icon: 'ri-close-circle-line',
      color: '#f87171',
      bg: 'rgba(248,113,113,0.08)',
      border: 'rgba(248,113,113,0.15)',
    },
  };

  const cfg = config[status] ?? config.pending;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap"
      style={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      <div className="w-3 h-3 flex items-center justify-center">
        <i className={`${cfg.icon} text-xs`} />
      </div>
      {cfg.label}
    </span>
  );
}

function MethodBadge({ method, isMock }: { method: string; isMock: boolean }) {
  // Mock payments: amount=$0, show a special "Simulado" badge instead of Wompi
  if (isMock) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap"
        style={{
          backgroundColor: 'rgba(251,146,60,0.10)',
          color: '#fb923c',
          border: '1px solid rgba(251,146,60,0.22)',
        }}
      >
        <div className="w-3 h-3 flex items-center justify-center">
          <i className="ri-test-tube-line text-xs" />
        </div>
        Simulado
      </span>
    );
  }

  const config: Record<string, { label: string; icon: string; color: string; bg: string; border: string }> = {
    wompi: {
      label: 'Wompi',
      icon: 'ri-bank-card-line',
      color: 'rgba(255,255,255,0.55)',
      bg: 'rgba(255,255,255,0.05)',
      border: 'rgba(255,255,255,0.10)',
    },
    cash: {
      label: 'Efectivo',
      icon: 'ri-money-dollar-circle-line',
      color: '#a3e635',
      bg: 'rgba(163,230,53,0.08)',
      border: 'rgba(163,230,53,0.18)',
    },
    transfer: {
      label: 'Transferencia',
      icon: 'ri-exchange-dollar-line',
      color: '#60a5fa',
      bg: 'rgba(96,165,250,0.08)',
      border: 'rgba(96,165,250,0.18)',
    },
  };

  const cfg = config[method] ?? config.wompi;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap"
      style={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      <div className="w-3 h-3 flex items-center justify-center">
        <i className={`${cfg.icon} text-xs`} />
      </div>
      {cfg.label}
    </span>
  );
}

const SKELETON_ROWS = 8;

export default function AccountingTable({ payments, loading }: Props) {
  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }} >
      {/* Table header */}
      <div
        className="px-5 py-4 border-b flex items-center justify-between"
        style={{ borderColor: theme.colors.border }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 flex items-center justify-center rounded-lg"
            style={{ backgroundColor: 'rgba(52,211,153,0.10)' }}
          >
            <i className="ri-file-list-3-line text-sm text-emerald-400" />
          </div>
          <div>
            <h3 className="text-white text-sm font-semibold">Libro de transacciones</h3>
            <p className="text-xs" style={{ color: theme.colors.textMuted }}>
              Registro detallado de todos los pagos confirmados
            </p>
          </div>
        </div>
        <div
          className="text-xs px-2.5 py-1 rounded-full"
          style={{
            backgroundColor: 'rgba(52,211,153,0.08)',
            color: '#34d399',
            border: '1px solid rgba(52,211,153,0.15)',
          }}
        >
          {loading ? '—' : `${payments.length} registros`}
        </div>
      </div>

      {/* ── MOBILE CARD VIEW ─────────────────────────────────── */}
      <div className="md:hidden divide-y" style={{ borderColor: theme.colors.border }}>
        {loading && (
          [...Array(4)].map((_, i) => (
            <div key={i} className="p-4 animate-pulse space-y-2.5">
              <div className="flex justify-between">
                <div className="h-3.5 bg-white/8 rounded w-24" />
                <div className="h-5 bg-white/8 rounded-full w-20" />
              </div>
              <div className="h-3 bg-white/6 rounded w-36" />
              <div className="flex gap-2">
                <div className="h-5 bg-white/6 rounded-full w-20" />
                <div className="h-5 bg-white/6 rounded-full w-20" />
              </div>
              <div className="flex justify-between items-center">
                <div className="h-3 bg-white/5 rounded w-28" />
                <div className="h-4 bg-white/10 rounded w-24" />
              </div>
            </div>
          ))
        )}

        {!loading && payments.length === 0 && (
          <div className="px-5 py-14 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                <i className="ri-receipt-line text-2xl" style={{ color: theme.colors.textDisabled }} />
              </div>
              <p className="text-sm" style={{ color: theme.colors.textMuted }}>Sin transacciones en este período</p>
              <p className="text-xs" style={{ color: theme.colors.textDisabled }}>Prueba cambiando el filtro de fecha</p>
            </div>
          </div>
        )}

        {!loading && payments.map((payment) => {
          const { date, time } = formatDateTime(payment.created_at);
          const appt = payment.appointment;
          const isMock = Number(payment.amount) === 0 && payment.method === 'wompi';

          return (
            <div key={payment.id} className="p-4 space-y-2.5">
              {/* Row 1: date + amount */}
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>{date}</p>
                  <p className="text-xs font-mono" style={{ color: theme.colors.textDisabled }}>{time}</p>
                </div>
                {isMock ? (
                  <div className="text-right">
                    <p className="text-sm font-bold font-mono" style={{ color: 'rgba(251,146,60,0.7)' }}>{formatCOP(0)}</p>
                    <p className="text-xs" style={{ color: 'rgba(251,146,60,0.45)' }}>sin cobro</p>
                  </div>
                ) : (
                  <span className="text-base font-bold font-mono" style={{ color: payment.type === 'remaining' ? '#34d399' : theme.colors.textPrimary }}>
                    {formatCOP(Number(payment.amount))}
                  </span>
                )}
              </div>

              {/* Row 2: client */}
              {appt && (
                <div>
                  <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>{appt.client_name}</p>
                  <p className="text-xs truncate" style={{ color: theme.colors.textDisabled }}>{appt.client_email}</p>
                </div>
              )}

              {/* Row 3: badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <TypeBadge type={payment.type} />
                <MethodBadge method={payment.method} isMock={isMock} />
                {appt && <AppointmentStatusBadge status={appt.status} />}
              </div>

              {/* Row 4: appointment date + reference */}
              {(appt?.appointment_date || payment.reference) && (
                <div className="flex items-center justify-between gap-2 pt-1 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  {appt?.appointment_date && (
                    <span className="text-xs font-mono flex items-center gap-1" style={{ color: theme.colors.textMuted }}>
                      <i className="ri-calendar-line text-[10px]" style={{ color: theme.colors.textDisabled }} />
                      {appt.appointment_date}
                      {appt.start_time && ` · ${appt.start_time.slice(0, 5)}`}
                    </span>
                  )}
                  {payment.reference && (
                    <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.04)', color: theme.colors.textMuted }}>
                      {payment.reference.slice(0, 12)}{payment.reference.length > 12 ? '…' : ''}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── DESKTOP TABLE VIEW ───────────────────────────────── */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
              <th className="text-left text-xs font-medium uppercase tracking-wider px-5 py-3.5" style={{ color: theme.colors.textDisabled }}>
                Fecha y hora
              </th>
              <th className="text-left text-xs font-medium uppercase tracking-wider px-5 py-3.5" style={{ color: theme.colors.textDisabled }}>
                Cliente
              </th>
              <th className="text-left text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden sm:table-cell" style={{ color: theme.colors.textDisabled }}>
                Estado cita
              </th>
              <th className="text-left text-xs font-medium uppercase tracking-wider px-5 py-3.5" style={{ color: theme.colors.textDisabled }}>
                Tipo
              </th>
              <th className="text-left text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden md:table-cell" style={{ color: theme.colors.textDisabled }}>
                Método
              </th>
              <th className="text-right text-xs font-medium uppercase tracking-wider px-5 py-3.5" style={{ color: theme.colors.textDisabled }}>
                Monto
              </th>
              <th className="text-left text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden lg:table-cell" style={{ color: theme.colors.textDisabled }}>
                Cita
              </th>
              <th className="text-left text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden xl:table-cell" style={{ color: theme.colors.textDisabled }}>
                Referencia
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <>
                {[...Array(SKELETON_ROWS)].map((_, i) => (
                  <tr key={i} className="animate-pulse" style={{ borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                    <td className="px-5 py-4">
                      <div className="space-y-1.5">
                        <div className="h-3 bg-white/8 rounded w-24" />
                        <div className="h-2.5 bg-white/5 rounded w-14" />
                      </div>
                    </td>
                    <td className="px-5 py-4"><div className="h-3 bg-white/6 rounded w-28" /></td>
                    <td className="px-5 py-4 hidden sm:table-cell"><div className="h-6 bg-white/6 rounded-full w-24" /></td>
                    <td className="px-5 py-4"><div className="h-6 bg-white/8 rounded-full w-20" /></td>
                    <td className="px-5 py-4 hidden md:table-cell"><div className="h-6 bg-white/6 rounded-full w-24" /></td>
                    <td className="px-5 py-4 text-right"><div className="h-4 bg-white/10 rounded w-20 ml-auto" /></td>
                    <td className="px-5 py-4 hidden lg:table-cell"><div className="h-3 bg-white/5 rounded w-20" /></td>
                    <td className="px-5 py-4 hidden xl:table-cell"><div className="h-3 bg-white/5 rounded w-24" /></td>
                  </tr>
                ))}
              </>
            )}

            {!loading && payments.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-20 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div
                      className="w-14 h-14 flex items-center justify-center rounded-full"
                      style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
                    >
                      <i className="ri-receipt-line text-2xl" style={{ color: theme.colors.textDisabled }} />
                    </div>
                    <p className="text-sm" style={{ color: theme.colors.textMuted }}>
                      Sin transacciones en este período
                    </p>
                    <p className="text-xs" style={{ color: theme.colors.textDisabled }}>
                      Prueba cambiando el filtro de fecha
                    </p>
                  </div>
                </td>
              </tr>
            )}

            {!loading && payments.map((payment, idx) => {
              const { date, time } = formatDateTime(payment.created_at);
              const isLast = idx === payments.length - 1;
              const appt = payment.appointment;
              const isMock = Number(payment.amount) === 0 && payment.method === 'wompi';

              return (
                <motion.tr
                  key={payment.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.03, 0.3) }}
                  className="hover:bg-white/2 transition-colors"
                  style={{ borderBottom: isLast ? 'none' : `1px solid rgba(255,255,255,0.04)` }}
                >
                  {/* Date & time */}
                  <td className="px-5 py-4">
                    <div>
                      <p className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>
                        {date}
                      </p>
                      <p className="text-xs font-mono mt-0.5" style={{ color: theme.colors.textDisabled }}>
                        {time}
                      </p>
                    </div>
                  </td>

                  {/* Client — right after date */}
                  <td className="px-5 py-4">
                    {appt ? (
                      <div>
                        <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                          {appt.client_name}
                        </p>
                        <p className="text-xs mt-0.5 truncate max-w-[160px]" style={{ color: theme.colors.textDisabled }}>
                          {appt.client_email}
                        </p>
                      </div>
                    ) : (
                      <span style={{ color: theme.colors.textDisabled }}>—</span>
                    )}
                  </td>

                  {/* Appointment status */}
                  <td className="px-5 py-4 hidden sm:table-cell">
                    {appt ? (
                      <AppointmentStatusBadge status={appt.status} />
                    ) : (
                      <span className="text-xs" style={{ color: theme.colors.textDisabled }}>—</span>
                    )}
                  </td>

                  {/* Type */}
                  <td className="px-5 py-4">
                    <TypeBadge type={payment.type} />
                  </td>

                  {/* Method */}
                  <td className="px-5 py-4 hidden md:table-cell">
                    <MethodBadge method={payment.method} isMock={isMock} />
                  </td>

                  {/* Amount */}
                  <td className="px-5 py-4 text-right">
                    {isMock ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span
                          className="text-sm font-bold font-mono"
                          style={{ color: 'rgba(251,146,60,0.7)' }}
                        >
                          {formatCOP(0)}
                        </span>
                        <span
                          className="text-xs font-mono"
                          style={{ color: 'rgba(251,146,60,0.45)' }}
                        >
                          sin cobro
                        </span>
                      </div>
                    ) : (
                      <span
                        className="text-sm font-bold font-mono"
                        style={{ color: payment.type === 'remaining' ? '#34d399' : theme.colors.textPrimary }}
                      >
                        {formatCOP(Number(payment.amount))}
                      </span>
                    )}
                  </td>

                  {/* Appointment date */}
                  <td className="px-5 py-4 hidden lg:table-cell">
                    {appt ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-calendar-line text-xs" style={{ color: theme.colors.textDisabled }} />
                        </div>
                        <div>
                          <p className="text-xs font-mono" style={{ color: theme.colors.textSecondary }}>
                            {appt.appointment_date}
                          </p>
                          <p className="text-xs font-mono" style={{ color: theme.colors.textDisabled }}>
                            {appt.start_time?.slice(0, 5)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: theme.colors.textDisabled }}>—</span>
                    )}
                  </td>

                  {/* Reference */}
                  <td className="px-5 py-4 hidden xl:table-cell">
                    {payment.reference ? (
                      <span
                        className="text-xs font-mono px-2 py-1 rounded"
                        style={{
                          backgroundColor: 'rgba(255,255,255,0.04)',
                          color: theme.colors.textMuted,
                        }}
                      >
                        {payment.reference.slice(0, 16)}{payment.reference.length > 16 ? '…' : ''}
                      </span>
                    ) : (
                      <span className="text-xs" style={{ color: theme.colors.textDisabled }}>—</span>
                    )}
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>{/* end hidden md:block */}

      {/* Footer totals row */}
      {!loading && payments.length > 0 && (
        <div
          className="px-5 py-4 border-t flex items-center justify-between flex-wrap gap-3"
          style={{ borderColor: theme.colors.border, backgroundColor: 'rgba(52,211,153,0.04)' }}
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calculator-line text-xs text-emerald-400" />
            </div>
            <span className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>
              Total del período ({payments.length} transacciones)
            </span>
          </div>
          <span className="text-base font-bold text-emerald-400 font-mono">
            {formatCOP(payments.reduce((sum, p) => sum + Number(p.amount), 0))}
          </span>
        </div>
      )}
    </div>
  );
}
